import { presentLexError } from '@/lib/user-notice';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { searchAll, searchArticlesSemantic, searchDofSemantic } from '@/lib/search';
import type { DofPublication, Law } from '@/types';
import type { ArticleSearchResult, SemanticArticleSearchResult } from '@/lib/search';

export function SearchPage() {
  const [query, setQuery] = useState('');
  const [laws, setLaws] = useState<Law[]>([]);
  const [articles, setArticles] = useState<(ArticleSearchResult | SemanticArticleSearchResult)[]>([]);
  const [dofItems, setDofItems] = useState<DofPublication[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searched, setSearched] = useState(false);
  const [mode, setMode] = useState<'literal' | 'semantic'>('literal');

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    const q = query.trim();
    if (!q) return;
    setError('');
    setLoading(true);
    setSearched(true);
    try {
      if (mode === 'semantic') {
        const [a, d] = await Promise.all([searchArticlesSemantic(q), searchDofSemantic(q)]);
        setLaws([]);
        setArticles(a);
        setDofItems(d);
      } else {
        const { laws: l, articles: a, dof: d } = await searchAll(q);
        setLaws(l);
        setArticles(a);
        setDofItems(d);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al buscar.');
      setLaws([]);
      setArticles([]);
      setDofItems([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="lex-page">
      {/* Page title */}
      <div style={{ padding: '4px 0 16px' }}>
        <p style={{ margin: 0, fontSize: 10, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--color-text-muted)' }}>
          Legislación
        </p>
        <h1 style={{ margin: '4px 0 0', fontSize: 22, fontWeight: 600, letterSpacing: '-0.02em', color: 'var(--color-text)' }}>
          Buscar
        </h1>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

        {/* Search bar + mode toggle */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {/* Mode toggle — simple tab style */}
          <div style={{ display: 'flex', background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 10, padding: 3, gap: 3 }}>
            {([
              ['literal', 'Literal'],
              ['semantic', 'Semántico'],
            ] as const).map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => setMode(value)}
                style={{
                  flex: 1,
                  padding: '6px 12px',
                  borderRadius: 8,
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: 12,
                  fontWeight: 600,
                  background: mode === value ? 'var(--color-navy)' : 'transparent',
                  color: mode === value ? 'var(--color-bg)' : 'var(--color-text-muted)',
                  transition: 'background 0.15s, color 0.15s',
                }}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Search input + submit */}
          <form onSubmit={handleSearch} style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1, position: 'relative' }}>
              <i className="ri-search-line" aria-hidden style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-muted)', fontSize: 14, pointerEvents: 'none' }} />
              <input
                type="search"
                className="lex-input"
                placeholder={mode === 'semantic' ? 'Describe el criterio o tema legal…' : 'Leyes, artículos o publicaciones DOF…'}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                autoFocus
                style={{ paddingLeft: 32 }}
              />
            </div>
            <button type="submit" className="lex-button-primary" disabled={loading} style={{ flexShrink: 0 }}>
              {loading ? 'Buscando…' : 'Buscar'}
            </button>
          </form>
        </div>

        {/* Error */}
        {error ? (
          <p style={{ margin: 0, color: 'var(--color-danger)', fontSize: 14 }}>{presentLexError(error)}</p>
        ) : null}

        {/* Pre-search prompt */}
        {!searched ? (
          <p style={{ color: 'var(--color-text-muted)', fontSize: 14, textAlign: 'center', padding: '24px 0', margin: 0 }}>
            {mode === 'semantic'
              ? 'El modo semántico prioriza resultados conceptualmente cercanos.'
              : 'Busca por nombre de ley, número de artículo o texto.'}
          </p>
        ) : loading ? (
          <p style={{ color: 'var(--color-text-muted)', fontSize: 14, textAlign: 'center', padding: '24px 0', margin: 0 }}>
            Buscando…
          </p>
        ) : (
          <>
            {/* Laws section — literal mode only */}
            {mode === 'literal' && (
              <ResultSection title="Leyes" count={laws.length} emptyMessage="Ninguna ley encontrada.">
                {laws.map((law) => (
                  <Link
                    key={law.id}
                    to={`/leyes/${law.id}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      padding: '14px 0',
                      borderBottom: '1px solid var(--color-border-soft)',
                      textDecoration: 'none',
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ margin: '0 0 3px', fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--color-accent)' }}>
                        Ley
                      </p>
                      <p style={{ margin: '0 0 3px', fontSize: 14, fontWeight: 600, color: 'var(--color-text)', lineHeight: 1.35 }}>
                        {law.title}
                      </p>
                      {law._count?.articles != null ? (
                        <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{law._count.articles} artículos</span>
                      ) : null}
                    </div>
                    <i className="ri-arrow-right-s-line" style={{ fontSize: 18, color: 'var(--color-text-muted)', flexShrink: 0 }} aria-hidden />
                  </Link>
                ))}
              </ResultSection>
            )}

            {/* Articles section */}
            <ResultSection title="Artículos" count={articles.length} emptyMessage="Ningún artículo encontrado.">
              {articles.map((art) => (
                <Link
                  key={art.id}
                  to={`/leyes/${art.lawId}/articulo/${art.id}`}
                  style={{
                    display: 'block',
                    padding: '14px 0',
                    borderBottom: '1px solid var(--color-border-soft)',
                    textDecoration: 'none',
                  }}
                >
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4, flexWrap: 'wrap' }}>
                    {art.law?.title ? (
                      <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--color-accent)' }}>
                        {art.law.title}
                      </span>
                    ) : null}
                    {'similarityScore' in art && typeof art.similarityScore === 'number' ? (
                      <span style={{ fontSize: 10, fontFamily: 'monospace', color: 'var(--color-text-muted)' }}>
                        {Math.round(art.similarityScore * 100)}%
                      </span>
                    ) : null}
                  </div>
                  <p style={{ margin: '0 0 4px', fontSize: 14, fontWeight: 600, color: 'var(--color-text)', lineHeight: 1.35 }}>
                    Artículo {art.articleNumber || art.number}
                    {(art.articleTitle || art.title) ? ` — ${art.articleTitle || art.title}` : ''}
                  </p>
                  {art.content ? (
                    <p style={{ margin: 0, fontSize: 13, color: 'var(--color-text-muted)', lineHeight: 1.5 }}>
                      {art.content.replace(/<[^>]+>/g, ' ').slice(0, 160)}…
                    </p>
                  ) : null}
                </Link>
              ))}
            </ResultSection>

            {/* DOF section */}
            <ResultSection title="DOF" count={dofItems.length} emptyMessage="Ninguna publicación DOF encontrada.">
              {dofItems.map((item) => (
                <article
                  key={item.dofId || item._id || item.url || item.title}
                  style={{
                    padding: '14px 0',
                    borderBottom: '1px solid var(--color-border-soft)',
                  }}
                >
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4, flexWrap: 'wrap' }}>
                    {item.source ? (
                      <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--color-accent)' }}>
                        {item.source}
                      </span>
                    ) : null}
                    {item.date ? (
                      <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
                        {new Date(item.date).toLocaleDateString('es-MX')}
                      </span>
                    ) : null}
                    {typeof item.similarityScore === 'number' ? (
                      <span style={{ fontSize: 10, fontFamily: 'monospace', color: 'var(--color-text-muted)' }}>
                        {Math.round(item.similarityScore * 100)}%
                      </span>
                    ) : null}
                  </div>
                  <p style={{ margin: '0 0 4px', fontSize: 14, fontWeight: 600, color: 'var(--color-text)', lineHeight: 1.35 }}>
                    {item.title}
                  </p>
                  {item.summary ? (
                    <p style={{ margin: '0 0 8px', fontSize: 13, color: 'var(--color-text-muted)', lineHeight: 1.55 }}>
                      {item.summary}
                    </p>
                  ) : null}
                  {item.url ? (
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-accent)', textDecoration: 'none' }}
                    >
                      Ver publicación ↗
                    </a>
                  ) : null}
                </article>
              ))}
            </ResultSection>
          </>
        )}
      </div>
    </main>
  );
}

function ResultSection({
  title,
  count,
  emptyMessage,
  children,
}: {
  title: string;
  count: number;
  emptyMessage: string;
  children: React.ReactNode[];
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {/* Section label */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--color-text-muted)' }}>
          {title}
        </span>
        <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.08em', color: 'var(--color-text-faint)' }}>
          {count}
        </span>
      </div>
      {children.length === 0 ? (
        <p style={{ margin: 0, color: 'var(--color-text-muted)', fontSize: 13, padding: '12px 0' }}>
          {emptyMessage}
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {children}
        </div>
      )}
    </div>
  );
}
